import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/state/AuthContext'
import axios from 'axios'
import { Check, X } from 'lucide-react'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { Button, IconButton, Notice, Select } from '@/components/play'

// OAuth endpoints live at /oauth/authorize, not /api/v1/oauth/authorize
const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1').replace(/\/api\/v1\/?$/, '')

interface AuthorizationInfo {
  status: 'pending'
  client: { name: string; uid: string }
  scopes: string[]
  resource?: string | null
  organizations?: { id: string; name: string }[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isAuthorizationInfo(value: unknown): value is AuthorizationInfo {
  return isRecord(value) && value.status === 'pending' &&
    isRecord(value.client) && typeof value.client.name === 'string' && typeof value.client.uid === 'string' &&
    Array.isArray(value.scopes) && value.scopes.every((scope) => typeof scope === 'string') &&
    (value.resource == null || typeof value.resource === 'string') &&
    (value.organizations === undefined || (Array.isArray(value.organizations) && value.organizations.every(
      (org) => isRecord(org) && typeof org.id === 'string' && typeof org.name === 'string'
    )))
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  events_read: 'View your events',
  events_write: 'Create, update and delete events',
  streams_read: 'View your cameras and their metrics',
  streams_write: 'Add, update and delete cameras',
  streams_control: 'Start and stop cameras',
  streams_invite: 'Open protected camera invitation handoffs',
  profile_read: 'View your profile information',
  profile_write: 'Update your profile',
}

const HOST_SCOPE_DESCRIPTIONS: Record<string, string> = {
  profile_read: 'View your name and chosen organization',
  events_read: 'View your events and workspace links',
  events_write: 'Create public events and edit their details',
  streams_read: 'View camera and live show connection evidence',
  streams_write: 'Add cameras, edit their settings and prepare a live show',
  streams_invite: 'Open protected camera invitation handoffs',
}

function oauthHeaders(token: string | null) {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export function OAuthAuthorizePage() {
  const { isAuthenticated, token } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [authInfo, setAuthInfo] = useState<AuthorizationInfo | null>(null)
  const [loading, setLoading] = useState(true)
  // Which consent action is in flight. Either one blocks both buttons; only its own button shows progress.
  const [pending, setPending] = useState<'connect' | 'cancel' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [organizationId, setOrganizationId] = useState('')

  useEffect(() => {
    if (!isAuthenticated) {
      const returnTo = `/oauth/authorize?${searchParams.toString()}`
      navigate(`/login?return_to=${encodeURIComponent(returnTo)}`, { replace: true })
      return
    }

    let cancelled = false
    const fetchAuthInfo = async () => {
      setLoading(true)
      setAuthInfo(null)
      setError(null)
      setOrganizationId('')
      try {
        const res = await axios.get<unknown>(
          `${API_ORIGIN}/oauth/authorize?${searchParams.toString()}`,
          // The browser document and authenticated JSON share this URL.
          { headers: { ...oauthHeaders(token), Accept: 'application/json', 'Cache-Control': 'no-cache' } }
        )
        if (!cancelled) {
          if (!isAuthorizationInfo(res.data)) {
            setError("We couldn't load the connection details. Try again.")
            return
          }
          setAuthInfo(res.data)
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error_description || "We couldn't load this connection request.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAuthInfo()
    return () => { cancelled = true }
  }, [isAuthenticated, searchParams, navigate, token])

  const handleAuthorize = async () => {
    setPending('connect')
    try {
      const res = await axios.post<{ redirect_uri: string }>(
        `${API_ORIGIN}/oauth/authorize`,
        {
          client_id: searchParams.get('client_id'),
          redirect_uri: searchParams.get('redirect_uri'),
          response_type: searchParams.get('response_type'),
          scope: searchParams.get('scope'),
          state: searchParams.get('state'),
          code_challenge: searchParams.get('code_challenge'),
          code_challenge_method: searchParams.get('code_challenge_method'),
          resource: searchParams.get('resource'),
          organization_id: organizationId || undefined,
        },
        { headers: oauthHeaders(token) }
      )
      window.location.href = res.data.redirect_uri
    } catch (err: any) {
      setError(err?.response?.data?.error_description || "We couldn't connect the app. Try again.")
      setPending(null)
    }
  }

  const handleDeny = async () => {
    setPending('cancel')
    try {
      const res = await axios.delete<{ redirect_uri: string }>(
        `${API_ORIGIN}/oauth/authorize?${searchParams.toString()}`,
        { headers: oauthHeaders(token) }
      )
      window.location.href = res.data.redirect_uri
    } catch {
      navigate('/', { replace: true })
    }
  }

  if (loading) {
    return (
      <main className="play-theme flex min-h-screen items-center justify-center">
        <ContentSkeleton label="Loading authorization" className="max-w-md px-6" />
      </main>
    )
  }

  if (error && !authInfo) {
    return (
      <main className="play-theme flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md space-y-6">
          <h1 className="text-3xl font-bold text-neutral-10">Connection unavailable</h1>
          <Notice tone="error" alert>{error}</Notice>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </main>
    )
  }

  if (!authInfo) return null

  return (
    <main className="play-theme flex min-h-screen items-center justify-center px-6 ph-no-capture ph-no-record">
      <div className="w-full max-w-md space-y-8 py-12">
        <PlayLogo />
        <div>
          <h1 className="text-3xl font-bold text-neutral-10">Connect {authInfo.client.name}</h1>
          <p className="mt-xs text-neutral-7">Choose what this app can access.</p>
        </div>
        <div className="space-y-6">
          {!!authInfo.organizations?.length && (
            <Select
              label="Organization"
              placeholder="Choose an organization"
              items={authInfo.organizations.map((org) => ({ value: org.id, label: org.name }))}
              value={organizationId || null}
              onValueChange={(value) => setOrganizationId(value ?? '')}
              disabled={pending !== null}
            />
          )}
          {authInfo.resource && !authInfo.organizations?.length && (
            <Notice tone="error">No authorized organization is available. Ask your organizer for access.</Notice>
          )}
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-7">This will allow the app to:</p>
            <ul className="space-y-2">
              {authInfo.scopes.map((scope) => (
                <li key={scope} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                  {(authInfo.resource ? HOST_SCOPE_DESCRIPTIONS[scope] : SCOPE_DESCRIPTIONS[scope]) || scope}
                </li>
              ))}
            </ul>
          </div>

          {error && <Notice tone="error" alert>{error}</Notice>}
          <div className="flex items-center gap-3">
            <IconButton
              label="Cancel connection"
              onClick={handleDeny}
              loading={pending === 'cancel'}
              disabled={pending === 'connect'}
            >
              <X />
            </IconButton>
            <Button
              className="flex-1"
              onClick={handleAuthorize}
              loading={pending === 'connect'}
              disabled={pending === 'cancel' || (!!authInfo.resource && !organizationId)}
            >
              <Check aria-hidden="true" />
              {pending === 'connect' ? 'Connecting' : 'Connect'}
            </Button>
          </div>
        </div>
      </div>
    </main>
  )
}
